import { RevealSpinner } from "../../design-system/utils/Spinner/Spinner.jsx";
import tokens from "../../design-system/tokens";
import {
  purpleDark,
  purpleLight,
} from "../../design-system/primitives/accents.css.ts";
import {
  darkMode,
  lightMode,
} from "../../design-system/primitives/modes.css.ts";
import cx from "../../lib/cx.js";
import { shellStyles as styles } from "./shell.css.ts";

/**
 * Renders the application's base layout shell, including optional components such as a header and bottom section.
 *
 * @param {Object} params - The parameters for configuring the BaseShell.
 * @param {React.ReactNode} params.children - The main content to render inside the base shell.
 * @param {React.ReactNode} [params.bottom] - An optional component or section to render at the bottom of the layout.
 * @returns {JSX.Element} The rendered BaseShell component with the specified layout and configurations.
 */
export function BaseShell({ children, bottom }) {
  return (
    <>
      <div className={styles.pageContainer({ marginTop: true })} id={"page"}>
        {children}
      </div>

      {bottom}
    </>
  );
}

/**
 * Wraps shell content. Sets the correct background color of the content.
 *
 * @param {object} props - The component props.
 * @param {ReactNode} props.children - The content to be rendered inside the shell content.
 * @param {string} [props.bg=tokens.color.full.gray300] - The background color of the shell content.
 * @param {object} [props.additionalStyles={}] - Additional custom styles to be applied to the shell content.
 * @param {"dark"|"light"} [props.theme="dark"] - Which mode/accent theme to apply. Defaults to dark; pass "light" for light (e.g. white) surfaces so theme tokens (accents, text) resolve correctly.
 * @param {React.Ref} ref - A reference to the shell content component.
 * @returns {ReactNode} - The rendered shell content.
 */
export const ShellContent = ({
  children,
  bg = tokens.color.full.gray300,
  additionalStyles = {},
  theme = "dark",
}) => {
  const customStyles = {
    background: bg,
    ...additionalStyles,
  };
  const themeClass =
    theme === "light" ? cx(lightMode, purpleLight) : cx(darkMode, purpleDark);
  return (
    <div className={cx(styles.contentWrapper, themeClass)} style={customStyles}>
      {children}
    </div>
  );
};

/**
 * Extends the BaseShell with a loading spinner.
 *
 * @param {Object} props - The props object.
 * @param {React.ReactNode} [props.children] - The content to be rendered inside the shell.
 * @param {boolean} props.isLoading - Whether a loading spinner should be shown.
 * @returns {JSX.Element} - The rendered shell component.
 */
function DefaultShell({ children, isLoading }) {
  return (
    <BaseShell>
      <RevealSpinner show={isLoading} />
      {children}
    </BaseShell>
  );
}

/**
 * A shell component that renders its children without a header nav.
 *
 * @param {Object} props The component props.
 * @param {React.ReactNode} [props.children] The content to be rendered inside the shell — absent while it is only showing the spinner.
 * @param {*} [props.bottom] The bottom configuration or UI element passed to the base shell.
 * @param {*} [props.bg] The background configuration or style passed to the base shell.
 * @param {boolean} [props.isLoading] Whether a loading spinner should be shown.
 * @return {JSX.Element} The rendered HeaderLessShell component.
 */
export function HeaderLessShell({ children, bottom, bg, isLoading }) {
  return (
    <BaseShell bottom={bottom} bg={bg}>
      <RevealSpinner show={isLoading} />
      {children}
    </BaseShell>
  );
}

/**
 * Renders a mini shell component with optional loading spinner; no header, bottom or navigation.
 *
 * @param {Object} props - The props object containing the following properties:
 *   - children {JSX.Element} - The content to be displayed inside the mini shell.
 *   - isLoading {boolean} - Indicates whether a loading spinner should be displayed.
 *
 * @return {JSX.Element} - The rendered mini shell component.
 */
export function MiniShell({ children, isLoading }) {
  return (
    <>
      <RevealSpinner show={isLoading} />
      {children}
    </>
  );
}

export default DefaultShell;
